import { Component, HostListener, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxEditorModule, Editor, Toolbar } from 'ngx-editor';
import { CreateProjectService } from '../services/create-project.service';
import { DashboardService } from '../services/dashboard.service';
import { NotificationService } from '../services/notification.service';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import { RoleViewService } from '../services/role-view.service';
import { UserDashboardService } from '../services/user-dashboard.service';
import { MatDialog } from '@angular/material/dialog';
import { OpendownlodfilesComponent } from './opendownlodfiles/opendownlodfiles.component';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';
type PreviewType = 'pdf' | 'docx' | 'xls' | 'image' | 'unsupported';
enum SubTaskStatus {
  UNASSIGNED = 'Unassigned',
  IN_PROGRESS = 'In Progress',
  PENDING_RRE_REVIEW = 'Pending RRE Review',
  PENDING_RAES_REVIEW = 'Pending RAES Review',
  COMPLETED = 'Completed',
  OVERDUE = 'Overdue',
  SUBMITTED = 'Submitted',
  NOT_STARTED = 'Not Started'
}
interface Activity {
  activity_type?: string;
  activityType?: string;
  activity_description?: string;
}
@Component({
  selector: 'app-detailed-screen',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    NgxEditorModule,
    TutorialVideoWidgetComponent,
  ],
  templateUrl: './dashboard-detailed-screen.component.html',
  styleUrl: './dashboard-detailed-screen.component.css'
})
export class DashboardDetailedScreenComponent implements OnInit, OnDestroy {
  readonly adminTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;
  projectName: string | null = null;
  projectId: string | null = null;
  currentProject: any = null;
  scrHeight = '';
  escalationEditor!: Editor;
  readonly subTaskStatus = SubTaskStatus;
  private readonly NO_TASK_SELECTED = 'No task selected';
  private readonly NO_PROJECT_SELECTED = 'No project selected';
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'bullet_list'],
    ['link', 'align_left'],
  ];

  projectDetails: any = {
    projectName: '',
    projectCode: '',
    contactPerson: '',
    status: '',
    dueDate: '',
    lastUpdated: '',
    taskProgress: { completed: 0, total: 0 },
    overallProgress: 0
  };

  emailDetails: any = { subject: '', email_body: '' };

  quickStats = [
    { label: SubTaskStatus.UNASSIGNED, count: 0, icon: 'person_off', color: 'gray' },
    { label: SubTaskStatus.IN_PROGRESS, count: 0, icon: 'trending_up', color: 'blue' },
    { label: SubTaskStatus.PENDING_RRE_REVIEW, count: 0, icon: 'description', color: 'orange' },
    { label: SubTaskStatus.PENDING_RAES_REVIEW, count: 0, icon: 'description', color: 'orange' },
    { label: SubTaskStatus.COMPLETED, count: 0, icon: 'check_circle', color: 'green' },
    { label: SubTaskStatus.OVERDUE, count: 0, icon: 'error', color: 'red' }
  ];

  // Status Overview filter
  selectedStatusFilters: string[] = [];
  isStatusDropdownOpen = false;
  statusFilterOptions: string[] = [SubTaskStatus.UNASSIGNED, SubTaskStatus.IN_PROGRESS, SubTaskStatus.PENDING_RRE_REVIEW, SubTaskStatus.PENDING_RAES_REVIEW, SubTaskStatus.COMPLETED, SubTaskStatus.OVERDUE];
  userRole = '';

  workflowSteps: string[] = [
    SubTaskStatus.UNASSIGNED,
    SubTaskStatus.IN_PROGRESS,
    SubTaskStatus.PENDING_RRE_REVIEW,
    SubTaskStatus.PENDING_RAES_REVIEW,
    SubTaskStatus.COMPLETED
  ];

  getWorkflowStepIndex(): number {
    const status = (this.projectDetails.status || '').toLowerCase();
    const map: Record<string, number> = {
      [SubTaskStatus.UNASSIGNED.toLowerCase()]: 0,
      [SubTaskStatus.IN_PROGRESS.toLowerCase()]: 1,
      [SubTaskStatus.PENDING_RRE_REVIEW.toLowerCase()]: 2,
      [SubTaskStatus.PENDING_RAES_REVIEW.toLowerCase()]: 3,
      [SubTaskStatus.COMPLETED.toLowerCase()]: 4
    };
    return map[status] ?? 0;
  }

  getWorkflowProgressWidth(): number {
    const stepIndex = this.getWorkflowStepIndex();
    if (stepIndex <= 0) {
      return 0;
    }
    if (stepIndex >= this.workflowSteps.length - 1) {
      return 100;
    }
    return (stepIndex / (this.workflowSteps.length - 1)) * 100;
  }

  get filteredQuickStats() {
    if (this.selectedStatusFilters.length === 0) {
      return this.quickStats;
    }
    return this.quickStats.filter(stat => this.selectedStatusFilters.includes(stat.label));
  }

  get filteredSubTasks() {
    if (this.selectedStatusFilters.length === 0) {
      return this.subTasks;
    }
    const filtersLower = new Set(this.selectedStatusFilters.map(f => f.toLowerCase()));
    return this.subTasks.filter(task => filtersLower.has(task.status?.toLowerCase()));
  }

  get isDownloadAllDisabled(): boolean {
    if (!this.subTasks.length) {
      return true;
    }
    return this.subTasks.some(task => task.uploadedDocumentsData === null);
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
  }

  toggleStatusFilter(status: string, event: Event): void {
    event.stopPropagation();
    const index = this.selectedStatusFilters.indexOf(status);
    if (index > -1) {
      this.selectedStatusFilters.splice(index, 1);
    } else {
      this.selectedStatusFilters.push(status);
    }
  }

  isStatusSelected(status: string): boolean {
    return this.selectedStatusFilters.includes(status);
  }

  toggleAllStatuses(event: Event): void {
    event.stopPropagation();
    if (this.selectedStatusFilters.length === this.statusFilterOptions.length) {
      this.selectedStatusFilters = [];
    } else {
      this.selectedStatusFilters = [...this.statusFilterOptions];
    }
  }

  get isAllStatusesSelected(): boolean {
    return this.selectedStatusFilters.length === this.statusFilterOptions.length;
  }

  getSelectedStatusCount(): number {
    return this.selectedStatusFilters.length;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.status-dropdown-container')) {
      this.isStatusDropdownOpen = false;
    }
  }

  clearStatusFilters(): void {
    this.selectedStatusFilters = [];
  }

  departmentsInvolved: any[] = [];
  collectionDetails: any = {
    primaryCollection: '',
    subCollections: []
  };

  subTasks: any[] = [];
  expandedTaskId: string | null = null;
  showActivityStream: { [key: string]: boolean } = {};
  newActivityComment: { [key: string]: string } = {};

  showFileVersions: { [key: string]: boolean } = {};
  expandedFileIds = new Set<string>();
  fileVersions: any[] | null = null;

  showEscalateModal = false;
  showChangeAssigneeModal = false;
  showEmailReminderModal = false;
  showReviewPanel = false;
  currentTask: any = null;
  currentEmailTask: any = null;
  emailReminderToAddress = '';
  reviewComments = '';
  emailReminderSubject = '';
  emailReminderMessage = '';
  emailReminderCc = '';
  emailReminderCcArray: any[] = [];
  emailReminderCcInput = '';
  emailReminderSearchTerm = '';
  availableEmailReminderRecipients: any[] = [];
  emailReminderCcSearchTerm = '';
  availableEmailReminderCcRecipients: any[] = [];
  emailMessageControl: FormControl = new FormControl('');
  escalationEmailContent = '';
  escalationReasonControl: FormControl = new FormControl('');
  escalationReason = '';
  reviewCommentsError = '';
  emailMessageEditor!: Editor;
  searchAssigneeQuery = '';
  selectedNewAssignee: any = null;
  transferNotes = '';
  searchEscalateQuery = '';
  selectedEscalateTarget: any = null;
  escalateToField = '';
  escalationPriorityLevel = '';
  private assigneeSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private escalateSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private emailReminderSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private emailReminderCcSearchTimer: ReturnType<typeof setTimeout> | null = null;
  availableUsers: any[] = [];
  departmentUsers: any[] = [];
  baseEscalationManagers: any[] = [];
  cachedTeamMembers: Record<string, any> = {};
  workDayId: any;
  userName = '';
  availableEscalationManagers: any[] = [];
  selectedCcEmails: any[] = [];
  escalateCcInput = '';
  project: any;

  @ViewChild('docxContainer') docxContainerRef!: ElementRef<HTMLDivElement>;

  isPreviewOpen = false;
  previewTitle = '';
  previewUrl: string | null = null;
  previewType: PreviewType | null = null;
  workbookCache: any = null;
  sheetNames: string[] = [];
  selectedSheetIndex = 0;
  excelHtml = '';
  _objectUrls: string[] = [];
  private _previewHistoryState = false;
  private _ignoreNextPop = false;
  private _onPopState: ((ev: PopStateEvent) => void) | null = null;

  get filteredUsers(): any[] {
    let filtered = this.availableUsers;

    // Filter out selected assignee
    if (this.selectedNewAssignee) {
      filtered = filtered.filter(user => user.userId !== this.selectedNewAssignee?.userId);
    }

    if (!this.searchAssigneeQuery.trim()) {
      return filtered;
    }
    const query = this.searchAssigneeQuery.toLowerCase();
    return filtered.filter(user =>
      user.tm_name.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  }

  get hasEmailDetails(): boolean {
    const details = this.emailDetails;
    if (!details) {
      return false;
    }

    const hasSubject = !!(details.subject && String(details.subject).trim());
    const hasBody = !!(details.email_body && String(details.email_body).trim());

    return hasSubject || hasBody;
  }

  get filteredEscalationTargets(): any[] {
    const filtered = this.availableEscalationManagers;
    const searchTerm = (this.searchEscalateQuery || this.escalateToField || '').trim();

    if (!searchTerm) {
      return filtered;
    }
    const query = searchTerm.toLowerCase();
    return filtered.filter(user =>
      user.tm_name?.toLowerCase().includes(query) ||
      user.email_address?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.job_title?.toLowerCase().includes(query) ||
      user.management_level?.toLowerCase().includes(query)
    );
  }


  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dashboardService: DashboardService,
    private readonly createProjectService: CreateProjectService,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly roleViewService: RoleViewService,
    private readonly userDashboardService: UserDashboardService,
    private readonly dialog: MatDialog

  ) {
    this.getScreenSize();
  }

  ngOnInit(): void {
    this.workDayId = this.createProjectService.getWorkdayId();
    this.userName = this.dashboardService.getUserName();
    this.userRole = this.roleViewService.getAdminAsRole()
    this.escalationEditor = new Editor();
    this.emailMessageEditor = new Editor();
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.fetchProjectDetailsById(String(this.projectId));
  }

  ngOnDestroy(): void {
    if (this.escalationEditor) {
      this.escalationEditor.destroy();
    }
    if (this.emailMessageEditor) {
      this.emailMessageEditor.destroy();
    }
    // Cleanup object URLs
    for (const url of this._objectUrls) {
      URL.revokeObjectURL(url);
    }
    this._objectUrls = [];
    // Cleanup history listener
    if (this._onPopState) {
      globalThis.removeEventListener('popstate', this._onPopState);
      this._onPopState = null;
    }
    if (this._previewHistoryState) {
      this._ignoreNextPop = true;
      history.back();
      this._previewHistoryState = false;
    }
    if (this.assigneeSearchTimer) {
      clearTimeout(this.assigneeSearchTimer);
      this.assigneeSearchTimer = null;
    }
    if (this.escalateSearchTimer) {
      clearTimeout(this.escalateSearchTimer);
      this.escalateSearchTimer = null;
    }
    if (this.emailReminderSearchTimer) {
      clearTimeout(this.emailReminderSearchTimer);
      this.emailReminderSearchTimer = null;
    }
    if (this.emailReminderCcSearchTimer) {
      clearTimeout(this.emailReminderCcSearchTimer);
      this.emailReminderCcSearchTimer = null;
    }
  }


  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: any): void {
    this.scrHeight = `${globalThis.innerHeight - 100}px`;
  }

  private fetchProjectDetailsById(projectId: string): void {
    this.dashboardService.fetchDashboardData(String(projectId)).subscribe({
      next: (response: any) => {
        const payload = Array.isArray(response?.payload) ? response.payload : [];
        this.project = payload.find((p: any) => {
          const payloadProjectId = String(p?.project_id || p?.project_details_id || '');
          return payloadProjectId === String(projectId);
        });
        console.info('Fetched project details:', this.project);

        if (this.project) {

          this.projectId = String(this.project.project_id || this.project.project_details_id || projectId);
          this.projectName = this.project.project_name || this.projectName;

          this.projectDetails = {
            projectName: this.project.project_name ?? '',
            projectCode: `${this.project.project_id}`,
            contactPerson: this.project.contact_person ?? 'Not Assigned',
            status: this.project.status ?? SubTaskStatus.IN_PROGRESS,
            dueDate: this.project.due_date ? new Date(this.project.due_date).toISOString() : '',
            lastUpdated: this.project.project_updated_at || new Date().toISOString(),
            taskProgress: { completed: 0, total: 0 },
            overallProgress: 0
          };
          this.emailDetails = {
            subject: this.project.subject ?? '',
            email_body: this.project.email_body ?? ''
          }

          this.departmentsInvolved = (this.project.departments || []).map((dept: any) => ({
            name: dept.department_name
          }));

          this.collectionDetails = {
            primaryCollection: this.project.collection_name || '',
            subCollections: (Array.isArray(this.project.sub_collection_name) ? this.project.sub_collection_name : [])
              .map((sub: any) => (typeof sub === 'string' ? sub : sub?.sub_collection_name || ''))
              .filter((name: string) => !!name)
          };

          this.fetchSubTasks();
          this.calculateProjectProgress();
          this.updateQuickStatsFromTasks();
          
        }
      }
    });
  }

  reloadPage(): void {
    if (this.projectId) {
      this.fetchProjectDetailsById(String(this.projectId));
    }
  }

  private fetchSubTasks(): void {
    const project = this.project;
    const activityCount = Number.parseInt(project?.activity || '0', 10);
    const projectStatus = project?.status ?? SubTaskStatus.IN_PROGRESS;

    if (project?.departments && project.departments.length > 0) {
      this.subTasks = project.departments.map((dept: any, index: number) => {
        return {
          taskId: dept.department_id || `task_${index}`,
          departmentId: dept.department_id ?? '',
          projectSubTaskId: dept.project_sub_task_id ?? 0,
          department: dept.department_name ?? '',
          assignedTo: dept.assigned_person_name ?? SubTaskStatus.UNASSIGNED,
          assigneeEmailId: dept.assigned_person_email_id ?? '',
          status: dept.sub_task_status || projectStatus,
          activities: activityCount,
          lastUpdated: dept.sub_task_updated_at ? new Date(dept.sub_task_updated_at).toISOString() : '',
          rreName: dept.rre_name ?? '',
          rreEmailAddress: dept.rre_email_address ?? '',
          activityStreamData: null,
          uploadedDocumentsData: null,
          noDocUploadsStatus: dept.no_doc_uploads_status || null
        };
      });

      // Load file versions and activity stream for all tasks on initial page load
      this.subTasks.forEach(task => {
        this.loadFileVersions(task);

        if (task.departmentId && project?.project_details_id) {
          this.dashboardService.fetchActivityStream(project.project_details_id, task.departmentId).subscribe({
            next: (response: any) => {
              const data = response?.payload ?? response;
              task.activityStreamData = Array.isArray(data) ? data : [];
              task.activities = task.activityStreamData.length;
              this.cdr.detectChanges();
            },
            error: () => {
              task.activityStreamData = [];
              task.activities = 0;
              this.cdr.detectChanges();
            }
          });
        }
      });
    } else {
      this.subTasks = [];
    }


  }

  private loadFileVersions(task: any): void {
    const project = this.project;

    if (!project?.project_details_id) {
      task.uploadedDocumentsData = [];
      return;
    }

    if (task.uploadedDocumentsData === null) {
      this.dashboardService
        .fetchUploadedDocuments(project.project_details_id.toString(), task.departmentId)
        .subscribe({
          next: (response: any) => {
            const documents = response?.payload || [];

            if (!documents.length) {
              task.uploadedDocumentsData = [];
              return;
            }

            const grouped = new Map<string, any>();

            documents.forEach((doc: any, index: number) => {
              if (doc.is_deleted) 
              {
                return;
              }

              const key = String(doc.raes_uploaded_file_id || doc.file_versioning_id || index);

              if (!grouped.has(key)) {
                grouped.set(key, {
                  fileId: key,
                  raes_uploaded_file_id: doc.raes_uploaded_file_id,
                  fileName: doc.file_name || `File-${index + 1}`,
                  document_status: doc.document_status || SubTaskStatus.NOT_STARTED,
                  versions: []
                });
              }

              const fileGroup = grouped.get(key);

              fileGroup.versions.push({
                versionNumber: Number(doc.version_number || 1),
                uploadedBy: doc.username || doc.uploaded_by || this.userName || 'Unknown',
                uploadedAt: doc.uploaded_at,
                uploadDate: doc.uploaded_at ? this.formatDate(doc.uploaded_at) : '',
                uploadTime: doc.uploaded_at
                  ? new Date(doc.uploaded_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                  : '',
                fileSize: doc.file_size || 'N/A',
                isDeleted: false,
                fileName: doc.file_name || 'Unknown',
                file_versioning_id: doc.file_versioning_id,
                file_name: doc.file_name || 'Unknown',
                isLatest: false
              });
            });

            task.uploadedDocumentsData = Array.from(grouped.values()).map((fileGroup: any) => {
              const uniqueVersions = new Map<number, any>();

              fileGroup.versions.forEach((v: any) => {
                const existing = uniqueVersions.get(v.versionNumber);
                if (!existing || new Date(v.uploadedAt).getTime() > new Date(existing.uploadedAt).getTime()) {
                  uniqueVersions.set(v.versionNumber, v);
                }
              });

              fileGroup.versions = Array.from(uniqueVersions.values());
              fileGroup.versions.sort((a: any, b: any) => b.versionNumber - a.versionNumber);

              if (fileGroup.versions.length > 0) {
                fileGroup.versions[0].isLatest = true;
                const versionOne = fileGroup.versions.find((v: any) => v.versionNumber === 1);
                fileGroup.fileName = versionOne ? versionOne.fileName : fileGroup.versions[fileGroup.versions.length - 1].fileName;
                fileGroup.file_versioning_id = fileGroup.versions[0].file_versioning_id;
                fileGroup.file_name = fileGroup.versions[0].file_name;
              }

              return fileGroup;
            });

            task.uploadedDocumentsData.sort(
              (a: any, b: any) => (b.versions[0]?.versionNumber || 0) - (a.versions[0]?.versionNumber || 0)
            );
            this.cdr.detectChanges();
          },
          error: () => {
            task.uploadedDocumentsData = [];
            this.cdr.detectChanges();
          }
        });
    }
  }

  getStatusMessage(_status: string): string {
    return '';
  }

  getStatusClass(status: string): string {
    const lowerStatus = status?.toLowerCase() || '';
    const statusClassMap: Record<string, string> = {
      [SubTaskStatus.UNASSIGNED.toLowerCase()]: 'status-unassigned',
      [SubTaskStatus.IN_PROGRESS.toLowerCase()]: 'status-in-progress',
      [SubTaskStatus.PENDING_RRE_REVIEW.toLowerCase()]: 'status-pending-rre-review',
      [SubTaskStatus.PENDING_RAES_REVIEW.toLowerCase()]: 'status-pending-raes-review',
      [SubTaskStatus.COMPLETED.toLowerCase()]: 'status-completed',
      [SubTaskStatus.OVERDUE.toLowerCase()]: 'status-overdue',
      [SubTaskStatus.SUBMITTED.toLowerCase()]: 'status-submitted'
    };
    return statusClassMap[lowerStatus] || 'status-unassigned';
  }

  getStatusDisplayLabel(status: string): string {
    return status || '';
  }

  private calculateProjectProgress(): void {
    const completedTasks = this.subTasks.filter(t => t.status?.toLowerCase() === SubTaskStatus.COMPLETED.toLowerCase()).length;
    const totalTasks = this.subTasks.length;
    const overallProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    this.projectDetails.taskProgress = {
      completed: completedTasks,
      total: totalTasks
    };
    this.projectDetails.overallProgress = overallProgress;
  }

  private updateQuickStatsFromTasks(): void {
    this.quickStats.forEach(stat => stat.count = 0);
    this.subTasks.forEach(task => {
      const status = task.status?.toLowerCase() || '';
      const statusIndexMap: Record<string, number> = {
        [SubTaskStatus.UNASSIGNED.toLowerCase()]: 0,
        [SubTaskStatus.IN_PROGRESS.toLowerCase()]: 1,
        [SubTaskStatus.PENDING_RRE_REVIEW.toLowerCase()]: 2,
        [SubTaskStatus.PENDING_RAES_REVIEW.toLowerCase()]: 3,
        [SubTaskStatus.COMPLETED.toLowerCase()]: 4,
        [SubTaskStatus.OVERDUE.toLowerCase()]: 5
      };
      const index = statusIndexMap[status];
      if (index !== undefined) {
        this.quickStats[index].count++;
      }
    });
  }

  private formatDate(dateString: string): string {
    if (!dateString)
    {
      return '';
    }
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
  }

  getStatIconColor(color: string): string {
    const colorMap: { [key: string]: string } = {
      'green': '#22c55e',
      'blue': '#0d6efd',
      'cyan': '#06b6d4',
      'purple': '#8b5cf6',
      'orange': '#f59e0b',
      'red': '#cc0000',
      'gray': '#6b7280'
    };
    return colorMap[color] || '#666';
  }

  getSoftBgColor(color: string): string {
    const colorMap: { [key: string]: string } = {
      'green': '#dcfce7',
      'blue': '#dbeafe',
      'cyan': '#cffafe',
      'purple': '#ede9fe',
      'orange': '#fef3c7',
      'red': '#fee2e2',
      'gray': '#f3f4f6'
    };
    return colorMap[color] || '#f3f4f6';
  }

  getFileIconType(fileName: string): string {
    if (!fileName) 
    {
      return 'default';
    }
    const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    const extMap: { [key: string]: string } = {
      'pdf': 'pdf',
      'xlsx': 'xls',
      'xls': 'xls',
      'xlsm': 'xls',
      'csv': 'xls',
      'docx': 'doc',
      'doc': 'doc',
      'png': 'image',
      'jpg': 'image',
      'jpeg': 'image',
      'gif': 'image'
    };
    return extMap[ext] || 'default';
  }

  toggleTaskExpand(taskId: string): void {
    if (this.expandedTaskId === taskId) {
      this.expandedTaskId = null;
      this.showActivityStream[taskId] = false;
      this.showFileVersions[taskId] = false;
    } else {
      this.expandedTaskId = taskId;
    }
  }

  viewActivityStream(taskId: string): void {
    console.info('Viewing activity stream for task:', taskId);

    this.showActivityStream[taskId] = !this.showActivityStream[taskId];

    if (!this.showActivityStream[taskId]) {
      return;
    }

    this.showFileVersions[taskId] = false;
    this.showReviewPanel = false;

    const taskIndex = this.subTasks.findIndex(t => t.taskId === taskId);
    if (taskIndex === -1) 
    {
      return;
    }

    const task = this.subTasks[taskIndex];
    const project = this.project;

    this.dashboardService
      .fetchActivityStream(project?.project_details_id || '', task.departmentId)
      .subscribe({
        next: (response: any) => {
          const data = response?.payload ?? response;
          const activities = Array.isArray(data) ? data : [];
          this.subTasks[taskIndex] = {
            ...this.subTasks[taskIndex],
            activityStreamData: activities
          };
          this.subTasks = [...this.subTasks];
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.info('Error fetching activity stream:', err);
        }
      });
  }

  private updateActivityStreamAfterAction(taskId: string): void {
    const taskIdx = this.subTasks.findIndex(t => t.taskId === taskId);
    if (taskIdx === -1) {
      return;
    }

    const task = this.subTasks[taskIdx];
    const project = this.project;

    this.dashboardService.fetchActivityStream(project?.project_details_id || '', task.departmentId).subscribe({
      next: (response: any) => {
        const data = response?.payload ?? response;
        const activities = Array.isArray(data) ? data : [];
        task.activityStreamData = activities;
        this.subTasks = [...this.subTasks];
        this.cdr.detectChanges();

        if (this.showActivityStream[taskId]) {
          this.viewActivityStream(taskId);
        }
      },
      error: (error) => {
        console.info('Error updating activity stream:', error);
      }
    });
  }

  toggleFileDisplay(taskId: string): void {
    this.showFileVersions[taskId] = !this.showFileVersions[taskId];
  }

  handleViewFileVersions(task: any): void {
    this.currentTask = task;

    if (task.status === 'Pending RAES Review') {
      if (this.showFileVersions[task.taskId]) {
        this.showReviewPanel = false;
        this.showFileVersions[task.taskId] = false;
      } else {
        this.showActivityStream[task.taskId] = false;
        this.showReviewPanel = true;
        this.showFileVersions[task.taskId] = true;
      }
    } else {
      if (!this.showFileVersions[task.taskId]) {
        this.showActivityStream[task.taskId] = false;
      }
      this.toggleFileDisplay(task.taskId);
    }
  }

  toggleFileExpand(fileId: string): void {
    if (this.expandedFileIds.has(fileId)) {
      this.expandedFileIds.delete(fileId);
    } else {
      this.expandedFileIds.add(fileId);
    }
  }

  viewFile(fileName: string, versionNumber: string): void {
    console.info(`View file: ${fileName} (v${versionNumber})`);
  }

  downloadFile(fileName: string, versionNumber: string): void {
    console.info(`Download file: ${fileName} (v${versionNumber})`);
  }

  download(file: any) {
    const id = file.file_versioning_id;
    if (id) {
      this.createProjectService.readDocument(this.workDayId, id).subscribe((res: any) => {
        if (!res?.body) {
          console.info('No body returned from API');
          return;
        }
        try {
          const base64Data = res.body.includes(',') ? res.body.split(',')[1] : res.body;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/octet-stream' });
          const url = globalThis.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.file_name || 'download';
          document.body.appendChild(a);
          a.click();
          a.remove();
          globalThis.URL.revokeObjectURL(url);
        } catch (error) {
          console.info('Download failed', error);
        }
      });
      return;
    }
    console.info('No file_versioning_id found', file);
  }

  private async _readFileBuffer(file: any, id: any): Promise<ArrayBuffer | null> {
    if (file.file?.arrayBuffer) { return file.file.arrayBuffer(); }
    if (!id) { return null; }

    try {
      const res: any = await new Promise((resolve, reject) => {
        this.createProjectService
          .readDocument(this.workDayId, id)
          .subscribe({ next: resolve, error: reject });
      });

      const body = res?.body;
      if (!body) { return null; }

      if (res?.isBase64Encoded || (typeof body === 'string' && !body.startsWith('http'))) {
        const base64 = body.includes(',') ? body.split(',')[1] : body;
        const binary = atob(base64);
        return Uint8Array.from(binary, c => c.codePointAt(0) ?? 0).buffer;
      }

      if (typeof body === 'string') {
        const response = await fetch(body);
        if (!response.ok) { throw new Error('Failed to fetch file'); }
        return response.arrayBuffer();
      }

      return null;
    } catch (err) {
      console.info('File fetch failed:', err);
      return null;
    }
  }

  private _renderDocxAsync(buffer: ArrayBuffer): void {
    setTimeout(async () => {
      if (!this.docxContainerRef?.nativeElement) {
        console.info('DOCX container not available');
        return;
      }

      this.docxContainerRef.nativeElement.innerHTML = '';

      try {
        await renderAsync(
          buffer,
          this.docxContainerRef.nativeElement,
          undefined,
          { className: 'docx' }
        );
      } catch (err) {
        console.info('DOCX render failed', err);
      }
    }, 0);
  }

  async preview(file: any) {
    if (!file) { return; }

    const name = (file.file_name || '').toLowerCase();
    const id = file.file_versioning_id;

    const isPdf = name.endsWith('.pdf');
    const isImage =
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      (file.type && String(file.type).toLowerCase().startsWith('image/'));
    const isDocx = name.endsWith('.docx');
    const isCsv = name.endsWith('.csv');
    const isExcel = isCsv || name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsm');

    if (!(isPdf || isImage || isDocx || isExcel)) {
      alert('File type not supported for preview');
      return;
    }

    const buffer = await this._readFileBuffer(file, id);

    if (!buffer) {
      let msg = 'Unable to load Excel';
      if (isPdf) {
        msg = 'Unable to load PDF';
      } else if (isImage) {
        msg = 'Unable to load image';
      } else if (isDocx) {
        msg = 'Unable to load DOCX';
      }
      alert(msg);
      return;
    }

    if (isPdf) {
      const url = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
      this._objectUrls.push(url);
      window.open(url, '_blank');
      return;
    }

    if (isImage) {
      const mime = name.endsWith('.png') ? 'image/png' : 'image/jpeg';
      const url = URL.createObjectURL(new Blob([buffer], { type: mime }));
      this._objectUrls.push(url);
      this.previewTitle = file.file_name;
      this.previewType = 'image';
      this.previewUrl = url;
      this.isPreviewOpen = true;
      this._addPreviewHistoryEntry();
      return;
    }

    if (isDocx) {
      this.previewTitle = file.file_name;
      this.previewType = 'docx';
      this.isPreviewOpen = true;
      this._addPreviewHistoryEntry();
      this._renderDocxAsync(buffer);
      return;
    }

    // Excel fallback
    this.previewTitle = file.file_name;
    this.previewType = 'xls';
    this.isPreviewOpen = true;
    this._addPreviewHistoryEntry();

    try {
      const workbook = isCsv
        ? XLSX.read(new TextDecoder('utf-8').decode(buffer), { type: 'string' })
        : XLSX.read(buffer, { type: 'array' });
      this.workbookCache = workbook;
      this.sheetNames = workbook.SheetNames || [];
      this.selectedSheetIndex = 0;
      this.renderSheetByIndex(this.selectedSheetIndex);
    } catch (err) {
      console.info('Excel render failed', err);
    }
  }

  renderSheetByIndex(index: number) {
    try {
      if (!this.workbookCache?.SheetNames || !this.workbookCache?.Sheets) {
        this.excelHtml = '<div class="xlsx-message">No workbook loaded.</div>';
        return;
      }
      const name = this.workbookCache.SheetNames[index];
      if (!name) {
        this.excelHtml = '<div class="xlsx-message">Sheet not found.</div>';
        return;
      }
      const sheet = this.workbookCache.Sheets[name];
      const tableHtml = XLSX.utils.sheet_to_html(sheet);
      const wrapped = `<div class="xlsx-preview">${tableHtml}</div>`;
      this.excelHtml = wrapped;
    } catch (err) {
      console.info('renderSheetByIndex failed', err);
      this.excelHtml = '<div class="xlsx-message">Unable to render sheet.</div>';
    }
  }

  private _addPreviewHistoryEntry() {
    try {
      if (this._previewHistoryState) 
      {
        return;
      }
      history.pushState({ cert_ui_preview: true }, '');
      this._previewHistoryState = true;
      this._onPopState = (_ev: PopStateEvent) => {
        if (this._ignoreNextPop) { this._ignoreNextPop = false; return; }
        if (this.isPreviewOpen) {
          this.isPreviewOpen = false;
          this.previewType = null;
          this.previewUrl = null;
          this.excelHtml = '';
          if (this._onPopState) {
            globalThis.removeEventListener('popstate', this._onPopState);
            this._onPopState = null;
          }
          this._previewHistoryState = false;
        }
      };
      globalThis.addEventListener('popstate', this._onPopState);
    } catch (e) {
      console.info('History push for preview failed', e);
    }
  }

  closePreview(_originatedFromPopstate = false) {
    this.isPreviewOpen = false;
    this.previewType = null;
    this.previewUrl = null;
    this.excelHtml = '';
    if (this._previewHistoryState) {
      if (this._onPopState) {
        globalThis.removeEventListener('popstate', this._onPopState);
        this._onPopState = null;
      }
      this._previewHistoryState = false;
      this._ignoreNextPop = false;
    }
  }

  closeReviewPanel(): void {
    this.showReviewPanel = false;
    this.currentTask = null;
    this.reviewComments = '';
    this.reviewCommentsError = '';
  }

  approveAndComplete(): void {
    if (!this.reviewComments.trim()) {
      this.reviewCommentsError = 'Review comments are required for approval.';
      return;
    }
    this.reviewCommentsError = '';

    if (!this.currentTask) {
      alert(this.NO_TASK_SELECTED);
      return;
    }

    const project = this.project;
    if (!project) {
      alert(this.NO_PROJECT_SELECTED);
      return;
    }


    const allTasksCompleted = this.subTasks
      .filter(t => t.taskId !== this.currentTask.taskId)
      .every(t => t.status?.toLowerCase() === SubTaskStatus.COMPLETED.toLowerCase());

    const payload: any = {
      projectId: project.project_details_id,
      departmentId: this.currentTask.departmentId,
      assigneeName: this.currentTask.assignedTo,
      assigneeId: this.currentTask.assigneeEmailId,
      status: SubTaskStatus.COMPLETED,
      userComments: this.reviewComments,
      userName: this.userName,
      updatedBy: this.userName,
      projectSubTaskId: this.currentTask.projectSubTaskId,
      assigneeEmailId: this.currentTask.assigneeEmailId,
      activityType: SubTaskStatus.COMPLETED,
      activityDescription: 'Task completed.',
      departmentName: this.currentTask.department,
      projectName: project.project_name,
      collectionName: project.collection_name,
      dueDate: this.formatDate(project.due_date),
      raesEmailAddress: this.userDashboardService.userEmail || '',
      raesId: this.userDashboardService.userId || '',
      raesName: this.userDashboardService.userName || '',
      contactPerson: this.projectDetails.contactPerson || '',
      rreEmailAddress: this.currentTask.rreEmailAddress || '',
      rreName: this.currentTask.rreName || '',
      noDocUploadsStatus: this.currentTask.noDocUploadsStatus || null
    };


    if (allTasksCompleted) {
      payload.projectStatus = SubTaskStatus.COMPLETED;
    }

    console.info('Approving and completing task with payload:', payload);

    this.dashboardService.updateDepartmentTaskAssignee(this.workDayId, payload)
      .subscribe({
        next: (response) => {
          console.info('Task approved and completed successfully:', response);
          this.currentTask.status = SubTaskStatus.COMPLETED;
          const taskIndex = this.subTasks.findIndex(t => t.taskId === this.currentTask.taskId);
          if (taskIndex !== -1) {
            this.subTasks[taskIndex].status = SubTaskStatus.COMPLETED;
          }
          if (allTasksCompleted) {
            this.projectDetails.status = SubTaskStatus.COMPLETED;
          }
          this.subTasks = [...this.subTasks];
          this.cdr.detectChanges();
          this.updateActivityStreamAfterAction(this.currentTask.taskId);
          this.closeReviewPanel();
          this.calculateProjectProgress();
          this.updateQuickStatsFromTasks();
          this.showSuccessMsg('Task reviewed and marked as completed.');
        },
        error: (error) => {
          console.info('Error reviewing task:', error);
          const errorMsg = error?.error?.message || 'Unable to review task. Please try again';
          this.showErrorMsg(errorMsg);
        }
      });
  }

  sendForRework(): void {
    if (!this.reviewComments.trim()) {
      this.reviewCommentsError = 'Review comments are required when sending for rework.';
      return;
    }
    this.reviewCommentsError = '';

    if (!this.currentTask) {
      alert(this.NO_TASK_SELECTED);
      return;
    }

    const project = this.project;
    if (!project) {
      alert(this.NO_PROJECT_SELECTED);
      return;
    }

    const payload = {
      projectId: project.project_details_id,
      departmentId: this.currentTask.departmentId,
      assigneeName: this.currentTask.assignedTo,
      assigneeId: this.currentTask.assigneeEmailId,
      status: SubTaskStatus.IN_PROGRESS,
      userComments: this.reviewComments,
      userName: this.userName,
      updatedBy: this.userName,
      projectSubTaskId: this.currentTask.projectSubTaskId,
      assigneeEmailId: this.currentTask.assigneeEmailId,
      activityType: 'Rework',
      activityDescription: ' Task moved to rework, status updated to "In progress".',
      departmentName: this.currentTask.department,
      projectName: project.project_name,
      collectionName: project.collection_name,
      dueDate: this.formatDate(project.due_date),
      contactPerson: this.projectDetails.contactPerson || '',
      rreEmailAddress: this.currentTask.rreEmailAddress || '',
      rreName: this.currentTask.rreName || '',
      raesEmailAddress: this.userDashboardService.userEmail || '',
      raesId: this.userDashboardService.userId || '',
      raesName: this.userDashboardService.userName || '',
    };

    console.info('Sending task for rework with payload:', payload);

    this.dashboardService.updateDepartmentTaskAssignee(this.workDayId, payload)
      .subscribe({
        next: (response) => {
          console.info('Task sent for rework successfully:', response);
          this.currentTask.status = SubTaskStatus.IN_PROGRESS;
          this.currentTask.noDocUploadsStatus = SubTaskStatus.NOT_STARTED;
          const taskIndex = this.subTasks.findIndex(t => t.taskId === this.currentTask.taskId);
          if (taskIndex !== -1) {
            this.subTasks[taskIndex].status = SubTaskStatus.IN_PROGRESS;
            this.subTasks[taskIndex].noDocUploadsStatus = SubTaskStatus.NOT_STARTED;
          }
          this.subTasks = [...this.subTasks];
          this.cdr.detectChanges();
          this.updateActivityStreamAfterAction(this.currentTask.taskId);
          this.currentTask.uploadedDocumentsData = null;
          this.loadFileVersions(this.currentTask);
          this.closeReviewPanel();
          this.calculateProjectProgress();
          this.updateQuickStatsFromTasks();
          this.showSuccessMsg('Task sent back for rework. User has been notified.');
        },
        error: (error) => {
          console.info('Error sending task for rework:', error);
          const errorMsg = error?.error?.message || 'Unable to send task for rework. Please try again';
          this.showErrorMsg(errorMsg);
        }
      });
  }

  selectAssignee(user: any): void {
    this.selectedNewAssignee = user;
    this.searchAssigneeQuery = '';
  }

  onAssigneeSearchInput(): void {
    if (this.assigneeSearchTimer) {
      clearTimeout(this.assigneeSearchTimer);
      this.assigneeSearchTimer = null;
    }

    const searchValue = this.searchAssigneeQuery.trim();
    if (!searchValue) {
      this.availableUsers = [...this.departmentUsers];
      return;
    }

    this.assigneeSearchTimer = setTimeout(async () => {
      try {
        const searchData = await this.userDashboardService.searchAllUsersDetails(searchValue);
        const searchedUsers = this.mapSearchAllUserData(searchData?.payload || []);
        this.availableUsers = this.mergeUniqueUsers(this.departmentUsers, searchedUsers)
          .filter((member: any) => member.tm_name !== this.currentTask?.assignedTo);
      } catch (error) {
        console.info('Error searching users:', error);
      }
    }, 500);
  }

  private mapSearchAllUserData(searchData: any[]): any[] {
    return (searchData || []).map((member: any) => ({
      tm_name: member.tm_name,
      email: member.email_address || member.email || '',
      userId: member.user_id || member.userId,
      department: member.department || member.department_name || ''
    }));
  }

  private mergeUniqueUsers(baseUsers: any[], searchUsers: any[]): any[] {
    const merged = [...(baseUsers || []), ...(searchUsers || [])];
    const uniqueByKey = new Map<string, any>();

    merged.forEach((user: any) => {
      const key = user.userId || user.email || user.tm_name;
      if (key && !uniqueByKey.has(key)) {
        uniqueByKey.set(key, user);
      }
    });

    return Array.from(uniqueByKey.values());
  }

  openChangeAssigneeModal(task: any): void {
    this.currentTask = task;
    this.showChangeAssigneeModal = true;
    this.availableUsers = [];
    this.departmentUsers = [];

    const project = this.project;

    if (project?.project_details_id && task.departmentId) {
      const cacheKey = `${project.project_id}_${task.departmentId}`;

      if (this.cachedTeamMembers[cacheKey]) {
        this.departmentUsers = this.cachedTeamMembers[cacheKey].filter((member: any) => member.tm_name !== task.assignedTo);
        this.availableUsers = [...this.departmentUsers];
      } else {
        this.dashboardService.fetchSelectedPrjDeptTeamMembers(project.project_details_id, task.departmentId).subscribe({
          next: (response: any) => {
            const teamMembers = this.mapSearchAllUserData(response || []);
            this.departmentUsers = (teamMembers || [])
              .filter((member: any) => member.tm_name !== task.assignedTo)
            this.availableUsers = [...this.departmentUsers];
            this.cachedTeamMembers[cacheKey] = this.departmentUsers;
          },
          error: (error) => {
            console.info('Error fetching team members:', error);
            this.departmentUsers = [];
            this.availableUsers = [];
          }
        });
      }
    }
  }

  closeChangeAssigneeModal(): void {
    this.showChangeAssigneeModal = false;
    this.currentTask = null;
    if (this.assigneeSearchTimer) {
      clearTimeout(this.assigneeSearchTimer);
      this.assigneeSearchTimer = null;
    }
    this.searchAssigneeQuery = '';
    this.selectedNewAssignee = null;
    this.transferNotes = '';
    this.departmentUsers = [];
  }


  submitChangeAssignee(): void {

    if (!this.selectedNewAssignee || !this.currentTask) {
      console.info('No assignee selected or no current task');
      this.showErrorMsg('Please select an assignee');
      return;
    }

    const currentTask = this.currentTask;
    const selectedAssignee = this.selectedNewAssignee;
    const project = this.project;
    if (!project) {
      console.info(this.NO_PROJECT_SELECTED);
      this.showErrorMsg(this.NO_PROJECT_SELECTED);
      return;
    }

    const currentStatus = currentTask.status?.toLowerCase();
    const statusToSend = currentStatus === SubTaskStatus.PENDING_RRE_REVIEW.toLowerCase() ? SubTaskStatus.PENDING_RRE_REVIEW : SubTaskStatus.IN_PROGRESS;

    const payload = {
      projectId: project.project_details_id,
      departmentId: currentTask.departmentId,
      assigneeName: selectedAssignee.tm_name,
      assigneeId: selectedAssignee.userId,
      status: statusToSend,
      userComments: this.transferNotes,
      userName: this.userName,
      updatedBy: this.userName,
      projectSubTaskId: currentTask.projectSubTaskId,
      assigneeEmailId: selectedAssignee.email,
      activityType: "Assigned",
      activityDescription: `Task assigned to ${selectedAssignee.tm_name} (${selectedAssignee.email})`,
      collectionName: project.collection_name,
      dueDate: this.formatDate(this.projectDetails.dueDate),
      departmentName: currentTask.department,
      projectName: project.project_name,
      contactPerson: project.contact_person,
      rreEmailAddress: currentTask.rreEmailAddress || '',
      rreName: currentTask.rreName || '',
      noDocUploadsStatus: currentTask.noDocUploadsStatus || null
    };

    console.info('Sending payload:', payload);
    const updatedStatus = currentStatus === SubTaskStatus.PENDING_RRE_REVIEW.toLowerCase() ? SubTaskStatus.PENDING_RRE_REVIEW : SubTaskStatus.IN_PROGRESS;
    this.dashboardService.updateDepartmentTaskAssignee(this.workDayId, payload)
      .subscribe({
        next: (response) => {
          console.info('Assignee updated successfully:', response);
          const assigneeName = selectedAssignee.tm_name;
          const assigneeEmail = response?.assigneeEmailId || selectedAssignee.email || '';
          currentTask.assignedTo = assigneeName;
          currentTask.assigneeEmailId = assigneeEmail;
          currentTask.status = updatedStatus;
          const index = this.subTasks.findIndex(t => t.departmentId === currentTask.departmentId);
          if (index !== -1) {
            this.subTasks[index].assignedTo = assigneeName;
            this.subTasks[index].assigneeEmailId = assigneeEmail;
            this.subTasks[index].status = updatedStatus;
          }

          this.updateActivityStreamAfterAction(currentTask.taskId);
          this.closeChangeAssigneeModal();
          this.calculateProjectProgress();
          this.updateQuickStatsFromTasks();
          this.showSuccessMsg(`${assigneeName} has been assigned to this task`);
        },
        error: (error) => {
          console.info('Error updating assignee:', error);
          const errorMsg = error?.error?.message || 'Unable to update assignee. Please try again';
          this.showErrorMsg(errorMsg);
        }
      });
  }

  selectEscalationTarget(target: any): void {
    this.selectedEscalateTarget = target;
    this.escalateToField = target.manager_email;
    this.searchEscalateQuery = '';
    console.info('Selected escalation target:', target);
  }

  onEscalateInput(): void {
    this.searchEscalateQuery = this.escalateToField;
    this.selectedEscalateTarget = null;

    if (this.escalateSearchTimer) {
      clearTimeout(this.escalateSearchTimer);
      this.escalateSearchTimer = null;
    }

    const searchValue = this.escalateToField.trim();
    if (!searchValue) {
      this.availableEscalationManagers = [...this.baseEscalationManagers];
      return;
    }

    this.escalateSearchTimer = setTimeout(async () => {
      try {
        const searchData = await this.userDashboardService.searchAllUsersDetails(searchValue);
        const searchedMembers = this.mapEscalationSearchData(searchData?.payload || []);
        this.availableEscalationManagers = this.mergeUniqueEscalationTargets(this.baseEscalationManagers, searchedMembers);
      } catch (error) {
        console.info('Error searching escalation targets:', error);
      }
    }, 500);
  }

  private mapEscalationSearchData(searchData: any[]): any[] {
    return (searchData || []).map((member: any) => ({
      user_id: member.user_id || member.userId,
      tm_name: member.tm_name || member.name,
      email_address: member.email_address || member.email || '',
      email: member.email || member.email_address || '',
      job_title: member.job_title || 'Team Member',
      management_level: member.management_level || 'N/A',
      manager_email: member.manager_email || member.email_address || member.email || ''
    }));
  }

  private mergeUniqueEscalationTargets(baseUsers: any[], searchUsers: any[]): any[] {
    const merged = [...(baseUsers || []), ...(searchUsers || [])];
    const uniqueByKey = new Map<string, any>();

    merged.forEach((user: any) => {
      const key = user.user_id || user.email_address || user.email || user.tm_name;
      if (key && !uniqueByKey.has(key)) {
        uniqueByKey.set(key, user);
      }
    });

    return Array.from(uniqueByKey.values());
  }

  toggleCcEmail(email: string): void {
    const index = this.selectedCcEmails.indexOf(email);
    if (index > -1) {
      this.selectedCcEmails.splice(index, 1);
    } else {
      this.selectedCcEmails.push(email);
    }
  }

  clearAllCcEmails(): void {
    this.selectedCcEmails = [];
  }

  addEscalateCcEmail(): void {
    const email = this.escalateCcInput.replaceAll(/,$/g, '').trim();
    if (!email || !this.isValidEmail(email) || this.selectedCcEmails.includes(email)) {
      return;
    }
    this.selectedCcEmails.push(email);
    this.escalateCcInput = '';
  }

  handleEscalateCcInput(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      if (value) { this.addEscalateCcEmail(); }
      return;
    }

    if (event.key === 'Backspace' && !value && this.selectedCcEmails.length > 0) {
      event.preventDefault();
      this.selectedCcEmails.pop();
    }
  }

  openEscalateModal(task: any): void {
    this.currentTask = task;
    this.showEscalateModal = true;
    this.availableEscalationManagers = [];
    this.baseEscalationManagers = [];
    this.selectedEscalateTarget = null;
    this.escalateToField = '';
    this.searchEscalateQuery = '';
    this.selectedCcEmails = [];
    this.escalateCcInput = '';

    const project = this.project;

    if (project?.project_details_id && task.departmentId) {
      const assignedToLower = (task.assignedTo || '').toLowerCase().trim();

      this.dashboardService.fetchSelectedPrjDeptTeamMembers(project.project_details_id, task.departmentId).subscribe({
        next: (response: any) => {
          const teamMembers = response?.payload || response || [];
          const uniqueMembers = new Map();
          let currentAssigneeManagerEmail = '';

          (teamMembers || []).forEach((member: any) => {
            if (member.user_id && member.tm_name) {
              const memberNameLower = member.tm_name.toLowerCase().trim();
              if (memberNameLower === assignedToLower && member.manager_email) {
                currentAssigneeManagerEmail = member.manager_email;
              }

              uniqueMembers.set(member.user_id, {
                user_id: member.user_id,
                tm_name: member.tm_name,
                email_address: member.email_address,
                job_title: member.job_title || 'Team Member',
                management_level: member.management_level || 'N/A',
                manager_email: member.manager_email
              });
            }
          });

          const allMembers = Array.from(uniqueMembers.values());

          if (currentAssigneeManagerEmail) {
            this.escalateToField = currentAssigneeManagerEmail;
          }

          this.availableEscalationManagers = allMembers.filter((m: any) =>
            m.tm_name?.toLowerCase().trim() !== assignedToLower
          );
          this.baseEscalationManagers = [...this.availableEscalationManagers];
          this.selectedCcEmails = allMembers.map((m: any) => m.email_address).filter(Boolean);
        },
        error: (error) => {
          console.info('Error fetching escalation managers:', error);
          this.baseEscalationManagers = [];
          this.availableEscalationManagers = [];
        }
      });
    }
  }

  closeEscalateModal(): void {
    this.showEscalateModal = false;
    this.currentTask = null;
    if (this.escalateSearchTimer) {
      clearTimeout(this.escalateSearchTimer);
      this.escalateSearchTimer = null;
    }
    this.searchEscalateQuery = '';
    this.selectedEscalateTarget = null;
    this.escalationReason = '';
    this.escalateToField = '';
    this.escalationPriorityLevel = '';
    this.selectedCcEmails = [];
    this.escalateCcInput = '';
    this.baseEscalationManagers = [];
    this.availableEscalationManagers = [];
    this.escalationReasonControl.reset();
  }

  canSubmitEscalate(): boolean {
    const targetValue = this.escalateToField.trim();
    const hasTarget = !!targetValue;
    const hasValidTarget = !!this.selectedEscalateTarget || this.isValidEmail(targetValue);
    const hasSubject = this.escalationReason.trim().length > 0;
    const hasMessage = this.hasMeaningfulEditorContent(this.escalationReasonControl.value || '');
    const hasTask = !!this.currentTask;

    return hasTask && hasTarget && hasValidTarget && hasSubject && hasMessage;
  }

  submitEscalate(): void {
    if (!this.escalateToField.trim()) {
      this.showErrorMsg('Please select an escalation target');
      return;
    }

    if (!this.escalationReason.trim()) {
      this.showErrorMsg('Please enter a subject for this escalation');
      return;
    }

    if (!this.hasMeaningfulEditorContent(this.escalationReasonControl.value || '')) {
      this.showErrorMsg('Please enter an escalation message');
      return;
    }

    if (!this.selectedEscalateTarget && !this.isValidEmail(this.escalateToField.trim())) {
      this.showErrorMsg('Please select a manager from the list or enter a valid email address');
      return;
    }

    if (!this.currentTask) {
      this.showErrorMsg(this.NO_TASK_SELECTED);
      return;
    }

    const htmlBody = this.escalationReasonControl.value || '';
    const toAddress = this.escalateToField;
    const toAddressName = this.selectedEscalateTarget?.manager_name || this.escalateToField;
    const project = this.project;

    const request = {
      projectId: project?.project_details_id || '',
      departmentId: this.currentTask?.departmentId || '',
      subTaskId: this.currentTask?.projectSubTaskId || this.currentTask?.taskId || this.currentTask?.id || '',
      userName: this.dashboardService.getUserName(),
      isReminder: false,
      toAddress: toAddress,
      toAddressName: toAddressName,
      ccAddress: this.selectedCcEmails,
      subjectContent: this.escalationReason,
      htmlBody: htmlBody,
      priorityLevel: this.escalationPriorityLevel
    };

    this.dashboardService.sendEmailReminder(this.workDayId, request).subscribe({
      next: (_response) => {
        this.showSuccessMsg('Task escalated successfully');
        this.updateActivityStreamAfterAction(this.currentTask.taskId);
        this.closeEscalateModal();
      },
      error: (error) => {
        console.info('Error escalating task:', error);
        const errorMsg = error?.error?.message || 'Failed to escalate task. Please try again.';
        this.showErrorMsg(errorMsg);
      }
    });
  }

  private readonly emailReminderRecipientNames = new Map<string, string>();

  private getDefaultReminderRecipient(task: any): { email: string; name: string } {
    const status = String(task?.status || '').trim().toLowerCase();
    const assignee = { email: task?.assigneeEmailId || '', name: task?.assignedTo || '' };
    const reviewer = { email: task?.rreEmailAddress || '', name: task?.rreName || '' };

    if ([SubTaskStatus.IN_PROGRESS.toLowerCase(), 'assigned', 'rework'].includes(status)) {
      return assignee;
    }
    if (status === SubTaskStatus.PENDING_RRE_REVIEW.toLowerCase()) {
      return reviewer;
    }

    // Preserve the existing recipient preference for other workflow stages.
    return reviewer.email || reviewer.name ? reviewer : assignee;
  }

  private rememberReminderRecipient(email: string, name: string): void {
    if (email && name) {
      this.emailReminderRecipientNames.set(email.trim().toLowerCase(), name);
    }
  }

  openEmailReminderModal(task: any): void {
    this.currentEmailTask = task;
    const recipient = this.getDefaultReminderRecipient(task);
    this.emailReminderRecipientNames.clear();
    this.rememberReminderRecipient(task?.assigneeEmailId, task?.assignedTo);
    this.rememberReminderRecipient(task?.rreEmailAddress, task?.rreName);
    this.rememberReminderRecipient(recipient.email, recipient.name);
    this.emailReminderToAddress = recipient.email;
    this.emailReminderSearchTerm = '';
    this.availableEmailReminderRecipients = [];
    this.emailReminderCcSearchTerm = '';
    this.availableEmailReminderCcRecipients = [];
    this.emailReminderSubject = '';
    this.emailReminderMessage = '';
    this.emailReminderCcArray = [];
    this.emailReminderCcInput = '';
    this.showEmailReminderModal = true;
  }

  closeEmailReminderModal(): void {
    this.showEmailReminderModal = false;
    this.currentEmailTask = null;
    this.emailReminderRecipientNames.clear();
    if (this.emailReminderSearchTimer) {
      clearTimeout(this.emailReminderSearchTimer);
      this.emailReminderSearchTimer = null;
    }
    if (this.emailReminderCcSearchTimer) {
      clearTimeout(this.emailReminderCcSearchTimer);
      this.emailReminderCcSearchTimer = null;
    }
    this.emailReminderToAddress = '';
    this.emailReminderSearchTerm = '';
    this.availableEmailReminderRecipients = [];
    this.emailReminderCcSearchTerm = '';
    this.availableEmailReminderCcRecipients = [];
    this.emailReminderSubject = '';
    this.emailReminderMessage = '';
    this.emailReminderCc = '';
    this.emailReminderCcArray = [];
    this.emailReminderCcInput = '';
    this.emailMessageControl.reset();
  }

  onEmailReminderToInput(): void {
    if (this.emailReminderSearchTimer) {
      clearTimeout(this.emailReminderSearchTimer);
      this.emailReminderSearchTimer = null;
    }

    const searchTerm = this.getToFieldSearchTerm();
    this.emailReminderSearchTerm = searchTerm;

    if (!searchTerm) {
      this.availableEmailReminderRecipients = [];
      return;
    }

    this.emailReminderSearchTimer = setTimeout(async () => {
      try {
        const searchData = await this.userDashboardService.searchAllUsersDetails(searchTerm);
        const recipients = this.mapSearchAllUserData(searchData?.payload || []);
        this.availableEmailReminderRecipients = recipients;
      } catch (error) {
        console.info('Error searching email reminder recipients:', error);
        this.availableEmailReminderRecipients = [];
      }
    }, 500);
  }

  selectEmailReminderRecipient(recipient: any): void {
    const selectedEmail = recipient?.email;
    if (!selectedEmail) {
      return;
    }

    this.rememberReminderRecipient(selectedEmail, recipient.tm_name);
    const typedParts = (this.emailReminderToAddress || '').split(',');
    const existingRecipients = typedParts
      .slice(0, -1)
      .map((entry: string) => entry.trim())
      .filter((entry: string) => !!entry);

    this.emailReminderToAddress = [...existingRecipients, selectedEmail].join(', ');
    this.emailReminderSearchTerm = '';
    this.availableEmailReminderRecipients = [];
  }

  private getToFieldSearchTerm(): string {
    const entries = (this.emailReminderToAddress || '').split(',');
    return (entries[entries.length - 1] || '').trim();
  }

  onEmailReminderCcInput(): void {
    if (this.emailReminderCcSearchTimer) {
      clearTimeout(this.emailReminderCcSearchTimer);
      this.emailReminderCcSearchTimer = null;
    }

    this.emailReminderCcSearchTerm = this.emailReminderCcInput.trim();

    if (!this.emailReminderCcSearchTerm) {
      this.availableEmailReminderCcRecipients = [];
      return;
    }

    this.emailReminderCcSearchTimer = setTimeout(async () => {
      try {
        const searchData = await this.userDashboardService.searchAllUsersDetails(this.emailReminderCcSearchTerm);
        const recipients = this.mapSearchAllUserData(searchData?.payload || [])
          .filter((recipient: any) => !this.emailReminderCcArray.includes(recipient.email));
        this.availableEmailReminderCcRecipients = recipients;
      } catch (error) {
        console.info('Error searching email reminder CC recipients:', error);
        this.availableEmailReminderCcRecipients = [];
      }
    }, 500);
  }

  selectEmailReminderCcRecipient(recipient: any): void {
    const selectedEmail = recipient?.email;
    if (!selectedEmail || this.emailReminderCcArray.includes(selectedEmail)) {
      return;
    }

    this.emailReminderCcArray.push(selectedEmail);
    this.emailReminderCcInput = '';
    this.emailReminderCcSearchTerm = '';
    this.availableEmailReminderCcRecipients = [];
  }

  private addCcEmailReminder(value: string): void {
    const email = value.replaceAll(/,$/g, '').trim();
    if (!email || !this.isValidEmail(email) || this.emailReminderCcArray.includes(email)) {
      return;
    }

    this.emailReminderCcArray.push(email);
    this.emailReminderCcInput = '';
    this.emailReminderCcSearchTerm = '';
    this.availableEmailReminderCcRecipients = [];
  }

  handleCcInput(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Check for Enter or comma
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();

      if (value) {
        this.addCcEmailReminder(value);
      }
    }

    // Handle Backspace to remove last email if input is empty
    if (event.key === 'Backspace' && !value && this.emailReminderCcArray.length > 0) {
      event.preventDefault();
      this.emailReminderCcArray.pop();
    }
  }

  removeCcEmailReminder(index: number): void {
    this.emailReminderCcArray.splice(index, 1);
  }

  private isValidEmail(email: string): boolean {
    // Domain labels exclude '.' so each repetition of (?:\.[^\s@.]+) has exactly one way
    // to tokenize, avoiding the ambiguous backtracking of overlapping [^\s@]+ groups.
    const emailRegex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/; // NOSONAR
    return emailRegex.test(email);
  }

  private parseEmailList(value: string): string[] {
    if (!value) {
      return [];
    }
    return value
      .split(',')
      .map((email: string) => email.trim())
      .filter((email: string) => !!email);
  }

  private hasMeaningfulEditorContent(value: string): boolean {
    if (!value) {
      return false;
    }
    const plainText = String(value)
      .replaceAll(/<[^>]*>/g, ' ')  //NOSONAR
      .replaceAll(/&nbsp;/gi, ' ')
      .trim();
    return plainText.length > 0;
  }

  canSendEmailReminder(): boolean {
    if (!this.currentEmailTask) {
      return false;
    }

    const toAddresses = this.parseEmailList(this.emailReminderToAddress);
    const hasValidToAddresses = toAddresses.length > 0 && toAddresses.every((email: string) => this.isValidEmail(email));
    const hasValidCcAddresses = this.emailReminderCcArray.every((email: string) => this.isValidEmail(email));
    const hasValidCcDraft = !this.emailReminderCcInput.trim()
      || this.isValidEmail(this.emailReminderCcInput.replaceAll(/,$/g, '').trim());
    const hasSubject = this.emailReminderSubject.trim().length > 0;
    const hasMessage = this.hasMeaningfulEditorContent(this.emailMessageControl.value || '');

    return hasValidToAddresses && hasValidCcAddresses && hasValidCcDraft && hasSubject && hasMessage;
  }

  sendEmailReminder(): void {
    if (!this.canSendEmailReminder()) {
      this.showErrorMsg('Please enter valid To email, Subject, and Message before sending');
      return;
    }

    const project = this.project;
    const htmlBody = this.emailMessageControl.value || '';
    const toAddress = this.parseEmailList(this.emailReminderToAddress);
    const ccAddress = this.emailReminderCcArray.filter((email: string) => email.trim());
    // Resolve names from the actual To addresses, including edited/multiple recipients.
    // For a manually typed address with no known name, use the address itself.
    const toAddressName = toAddress
      .map(email => this.emailReminderRecipientNames.get(email.toLowerCase()) || email)
      .join(', ');

    const request = {
      projectId: project?.project_details_id || '',
      departmentId: this.currentEmailTask?.departmentId || '',
      subTaskId: this.currentEmailTask?.projectSubTaskId || this.currentEmailTask?.taskId || this.currentEmailTask?.id || '',
      userName: this.dashboardService.getUserName(),
      isReminder: true,
      toAddress: toAddress,
      toAddressName: toAddressName,
      ccAddress: ccAddress,
      subjectContent: this.emailReminderSubject,
      htmlBody: htmlBody
    };

    this.dashboardService.sendEmailReminder(this.workDayId, request).subscribe({
      next: (_response) => {
        this.showSuccessMsg('Email reminder sent successfully');
        this.updateActivityStreamAfterAction(this.currentEmailTask.taskId);
        this.closeEmailReminderModal();
      },
      error: (error) => {
        console.info('Error sending email reminder:', error);
        this.showErrorMsg('Failed to send email reminder. Please try again.');
      }
    });
  }

  hasComments(activity: any): boolean {
    return activity.user_comments && activity.user_comments.trim().length > 0;
  }


  getActivityIconType(activity: Activity): string {
    const type = String(activity.activity_type ?? activity.activityType ?? '').toLowerCase();
    const description = String(activity.activity_description ?? '').toLowerCase();

    const matches = (keywords: string[]) =>
      keywords.some(k => type.includes(k) || description.includes(k));

    if (type === 'comment' || matches(['comment has been added'])) {
      return 'comment';
    }

    if (description === 'task completed.' || description === 'task completed') {
      return 'approved';
    }

    if (matches(['assign', 'task assigned'])) {
      return 'assigned';
    }

    if (
      matches(['rre']) &&
      matches(['reject', 'rework', 'review'])
    ) {
      return 'rre-review';
    }

    if (
      (type === 'no changes required' ||
        type === 'not responsible' ||
        matches(['status changed', 'status updated', 'has been updated to']) ||
        (description.includes('reviewed') && description.includes('status'))) &&
      !matches(['rework', 'reject'])
    ) {
      return 'status-update';
    }

    const rules = [
      { key: 'file-deleted', match: ['delete'] },
      { key: 'file-uploaded', match: ['upload'] },
      { key: 'rework', match: ['rework', 'reject'] },
      { key: 'reminder', match: ['reminder', 'email'] },
      { key: 'escalation', match: ['escalat'] },
      { key: 'skip', match: ['skip'] }
    ];

    for (const rule of rules) {
      if (matches(rule.match)) {
        return rule.key;
      }
    }

    return 'activity';
  }
  showSuccessMsg(msg: string): void {
    this.notificationService.showSuccess(msg);
  }

  showErrorMsg(msg: string): void {
    this.notificationService.showError(msg);
  }

  postActivityComment(task: any): void {
    const taskId = task.taskId;
    const comment = this.newActivityComment[taskId]?.trim();
    if (comment) {
      const project = this.dashboardService.getSelectedProject();
      const request = {
        projectId: project?.project_id || '',
        departmentId: task.departmentId,
        projectSubTaskId: task.projectSubTaskId,
        userComments: comment,
        userName: this.userName
      };

      this.dashboardService.postActivityComments(this.workDayId, request).subscribe({
        next: () => {
          const newActivity = {
            activity_type: 'Comment',
            activity_description: 'Comment has been added',
            user_comments: comment,
            created_by: this.userName,
            created_at: new Date().toISOString(),
            department_id: task.departmentId,
            user_name: this.userName
          };

          if (task.activityStreamData) {
            task.activityStreamData.unshift(newActivity);
            this.subTasks = [...this.subTasks];
            this.cdr.detectChanges();
          }

          this.newActivityComment[taskId] = '';
          this.showSuccessMsg('Comment added successfully');
        },
        error: () => {
          this.showErrorMsg('Failed to add comment. Please try again.');
        }
      });
    }
  }

  downloadAllFiles(): void {
    const departments = this.buildDepartmentDocuments();
    if (!departments.length) {
      this.showErrorMsg('No documents available to download.');
      return;
    }

    const dialogRef = this.dialog.open(OpendownlodfilesComponent, {
      width: '720px',
      maxWidth: '95vw',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: { departments }
    });

    dialogRef.afterClosed().subscribe((selectedIds: string[] | undefined) => {
      if (selectedIds?.length) {
        this.downloadByVersionIds(selectedIds);
      }
    });
  }

  private buildDepartmentDocuments(): Array<{ departmentId: string; departmentName: string; files: Array<{ id: string; fileName: string; status: string; versionNumber: number }> }> {
    return (this.subTasks || [])
      .map((task: any) => ({
        departmentId: String(task.departmentId || task.taskId || ''),
        departmentName: task.department || 'Department',
        files: (task.uploadedDocumentsData || [])
          // one entry per version, using that file's proper name + its version number
          .flatMap((file: any) =>
            (file.versions || []).map((version: any) => ({
              id: String(version.file_versioning_id || ''),
              fileName: version.file_name || version.fileName || file.fileName || file.file_name || 'Untitled',
              status: file.document_status || '',
              versionNumber: Number(version.versionNumber || 1)
            }))
          )
          .filter((f: any) => !!f.id)
      }))
      .filter((dept: any) => dept.files.length > 0);
  }

  private downloadByVersionIds(ids: string[]): void {
    if (!ids?.length) { return; }

    const payload = {
      projectName: this.projectName,
      ids: ids,
      workDayId: this.workDayId
    };

    this.createProjectService.documentReadUrls(payload).subscribe({
      next: (res: any) => {
        const result = this.extractZipDownloadResult(res);

        if (!result?.downloadUrl || (result.status && result.status !== 'ready')) {
          this.showErrorMsg(this.getDownloadErrorMessage(result));
          return;
        }

        this.triggerZipDownload(result.downloadUrl);

        const failureCount = Array.isArray(result.failures) ? result.failures.length : 0;
        if (failureCount > 0) {
          this.showErrorMsg(`ZIP created, but ${failureCount} file(s) could not be included.`);
        } else {
          this.showSuccessMsg('Download started.');
        }
      },
      error: () => {
        this.showErrorMsg('Bulk download API failed. Please try again.');
      }
    });
  }

  private getDownloadErrorMessage(result: { message?: string; reason?: string; failures?: any[] } | null): string {
    const failures = result && Array.isArray(result.failures) ? result.failures : [];
    const notFound = failures.filter(
      (f: any) => f?.reason === 'not_found_in_storage' || String(f?.error || '').includes('404')
    );

    if (failures.length > 0 && notFound.length === failures.length) {
      return 'No documents found in storage. The selected file(s) may have been removed.';
    }
    if (notFound.length > 0) {
      return `${notFound.length} of ${failures.length} selected document(s) were not found in storage.`;
    }
    return result?.message || result?.reason || 'Bulk download failed. Please try again.';
  }

  private extractZipDownloadResult(res: any): { status?: string; message?: string; reason?: string; downloadUrl?: string; expiresIn?: number; failures?: any[] } | null {
    let body = res?.body ?? res;

    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return null;
      }
    }

    if (!body || typeof body !== 'object') {
      return null;
    }

    return body;
  }

  private triggerZipDownload(url: string): void {
    // The presigned S3 URL already sets Content-Disposition: attachment with the filename,
    // so a plain anchor navigation downloads the ZIP without hitting S3 CORS.
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}
